import { IItemIdentifier } from "../interfaces/IItemIdentifier";
import { IPricedProduct } from "../interfaces/IPricedProduct";
import HttpClient from "./httpClient";
import { forkJoin } from "rxjs";
import { ApiEndpointResolver } from "./apiEndpointResolver";
import { ICartItemToAdd } from "wbm-cart-service/interfaces/cartItemToAdd";
import $ from "jquery";
import { productUrlService } from "./productUrlService";
import ConfigProps from "../constants/configProps";
import ConfigPropService from "./configPropService";
import { WbmMainHeaderApi } from "@wbmason/main-header-api";
import { ProductAvailabilityService } from "./productAvailabilityService";
import { IAvailabilityInfo } from "../interfaces/IAvailabilityInfo";
import { IShoppingLocation } from "../interfaces/IShoppingLocation";
import { AlertLevel } from "../enums/AlertLevel";
import { CartServiceAccessor } from "./cartServiceAccessor";
import AnalyticsService from "./analyticsService";
require("slick-carousel");

const productCardWidth = 215;
const spacingBuffer = 12;
const mobileBreakPoint = 768;

export const mzsAccountNumber = "wbm_mzs_account_number";

function getItemIdentifiers(
  merchZoneItemsNode: NodeListOf<Element>,
  coid: string
) {
  const itemIdentifiers: IItemIdentifier[] = [];

  merchZoneItemsNode.forEach((itemElement) => {
    const itemIdentifier = parseItemIdentifier(itemElement, coid);

    if (itemIdentifier) {
      const duplicateItem = itemIdentifiers.some((item) => {
        return compareItemIdentifiers(item, itemIdentifier);
      });

      if (!duplicateItem) {
        itemIdentifiers.push(itemIdentifier);
      }
    }
  });

  return itemIdentifiers;
}
function pricePermitPurchaseItems(itemIdentifiers: IItemIdentifier[]) {
  return HttpClient.post<IPricedProduct[]>(
    {
      baseUri: `${ApiEndpointResolver.getPricedProductUrl()}?applyRestrictions=true`,
      body: itemIdentifiers,
    },
    {
      withCredentials: true,
    }
  );
}
function parseItemIdentifier(itemElement: Element, coid: string) {
  const itemIdentifierParts = itemElement.getAttribute("data-item")?.split("|");

  if (itemIdentifierParts) {
    //only set attribute on first reference to parseItemIdentifier, don't pull coid from the data-item attribute
    if (coid && coid !== itemIdentifierParts[2]) {
      itemElement.setAttribute(
        "data-item",
        `${itemIdentifierParts[0]}|${itemIdentifierParts[1]}|${coid}`
      );
    }

    return convertToItemIdentifier(
      itemIdentifierParts[0],
      itemIdentifierParts[1],
      coid
    );
  }

  return null;
}
function compareItemIdentifiers(
  item: IItemIdentifier,
  itemIdentifier: IItemIdentifier
) {
  return (
    item.id === itemIdentifier.id &&
    item.unitId === itemIdentifier.unitId &&
    item.couponOfferCode === itemIdentifier.couponOfferCode
  );
}
function convertToItemIdentifier(
  id: string,
  unitId: string,
  couponOfferCode: string
) {
  const itemIdentifier: IItemIdentifier = {
    id: id,
    unitId: unitId,
    couponOfferCode: couponOfferCode,
  };

  return itemIdentifier;
}
function getBreakpointToScroll(
  slides: number,
  slidesToShow: number,
  emptySlidesAllowed: number = 0
) {
  let returnValue = 1;
  const remainingSlides = slides - slidesToShow;
  for (let i = slidesToShow; i > 1; i--) {
    if (remainingSlides % i <= emptySlidesAllowed) {
      returnValue = i;
      break;
    }
  }
  return returnValue;
}
function setSpinnerElement(targetElement: Element) {
  const targetElementContent = targetElement.firstElementChild;

  let spinnerElement = targetElement.querySelector(".mzs-spinner");

  if (spinnerElement) {
    targetElement.removeChild(spinnerElement);

    targetElement.removeAttribute("disabled");

    targetElementContent?.setAttribute("style", "display:flex;");
  } else {
    spinnerElement = createSpinnerElement();

    targetElement.setAttribute("disabled", "disabled");

    targetElementContent?.setAttribute("style", "display:none;");

    targetElement.appendChild(spinnerElement);
  }
}
function createSpinnerElement() {
  const spinnerElement = document.createElement("div");
  spinnerElement.className = "mzs-spinner";
  spinnerElement.setAttribute("role", "status");
  spinnerElement.setAttribute("aria-hidden", "true");

  spinnerElement.appendChild(document.createElement("div"));
  spinnerElement.appendChild(document.createElement("div"));
  spinnerElement.appendChild(document.createElement("div"));
  spinnerElement.appendChild(document.createElement("div"));
  return spinnerElement;
}
function createBreakPoint(
  breakPoint: number,
  slidesToShow: number,
  displayedRecommendations: number
) {
  if (breakPoint < mobileBreakPoint) {
    return {
      breakpoint: breakPoint,
      settings: {
        prevArrow: "<div></div>",
        nextArrow: "<div></div>",
        draggable: true,
        slidesToShow: slidesToShow,
        slidesToScroll: getBreakpointToScroll(
          displayedRecommendations,
          slidesToShow
        ),
      },
    };
  } else {
    return {
      breakpoint: breakPoint,
      settings: {
        draggable: false,
        slidesToShow: slidesToShow,
        slidesToScroll: getBreakpointToScroll(
          displayedRecommendations,
          slidesToShow
        ),
      },
    };
  }
}
function updateRecommendations(
  pricedMerchZoneItems: IPricedProduct[],
  merchZoneItemsNode: NodeListOf<Element>,
  merchZoneElement: Element,
  merchZoneId: string,
  containerWidth: number,
  containerHeight: number,
  displayAvailability: boolean,
  productAvailabilityAPIName: string,
  quantityMaxDigits: string,
  coid: string
) {
  let displayedRecommendations = 0;
  const pricedItemIdentifiers: IItemIdentifier[] = [];

  merchZoneItemsNode.forEach((itemElement) => {
    const itemIdentifier = parseItemIdentifier(itemElement, coid);

    if (itemIdentifier) {
      const pricedItem = pricedMerchZoneItems.find((item) => {
        return compareItemIdentifiers(
          convertToItemIdentifier(
            item.itemId,
            item.unitId,
            item.couponOfferCode
          ),
          itemIdentifier
        );
      });

      if (pricedItem) {
        const itemLinks = itemElement.querySelectorAll(".mzs-product-link");

        itemLinks.forEach((link) => {
          const itemUrl = productUrlService.getItemHref(
            itemIdentifier,
            true,
            link.getAttribute("icid")
          );

          link.setAttribute("href", itemUrl);
        });

        const itemPrice = itemElement.querySelector(".merch-price");
        if (itemPrice) {
          const price = pricedItem.sellPrice
            ? pricedItem.sellPrice
            : pricedItem.listPrice;

          itemPrice.innerHTML = price.toFixed(2);

          const qtyAddToCart: HTMLInputElement | null =
            itemElement.querySelector(".mzs-qty-add-to-cart");

          qtyAddToCart?.setAttribute("maxLength", quantityMaxDigits || "4");

          if (qtyAddToCart) {
            qtyAddToCart.addEventListener("input", (e) => {
              const validQtyPattern = /^[1-9][0-9]*$/;
              if (!validQtyPattern.test(qtyAddToCart.value)) {
                qtyAddToCart.value = "";
              }
            });
          }

          const btnAddToCart = itemElement.querySelector(
            ".mzs-btn-add-to-cart"
          );

          btnAddToCart?.addEventListener("click", () => {
            addToCart(itemIdentifier, btnAddToCart);
          });
        }

        displayedRecommendations++;
        pricedItemIdentifiers.push(itemIdentifier);
      } else {
        itemElement?.parentElement?.remove();
      }
    }
  });

  if (displayedRecommendations > 0 && merchZoneElement) {
    const merchSectionElement =
      merchZoneElement.parentElement?.parentElement?.parentElement;
    if (merchSectionElement) {
      merchSectionElement.setAttribute("style", "display:block;");

      AnalyticsService.emitCustomEvent("displayPricedMerchZone");

      const $merchZone: any = $("#" + merchZoneId);

      const responsive = [];
      let breakPointWidth = productCardWidth * 2 + spacingBuffer;
      let slidesToShow =
        (breakPointWidth - spacingBuffer) / productCardWidth - 1;
      while (breakPointWidth < containerWidth) {
        if (
          breakPointWidth > mobileBreakPoint &&
          breakPointWidth - productCardWidth < mobileBreakPoint
        ) {
          responsive.push(
            createBreakPoint(
              mobileBreakPoint,
              slidesToShow,
              displayedRecommendations
            )
          );
        }
        responsive.push(
          createBreakPoint(
            breakPointWidth,
            slidesToShow,
            displayedRecommendations
          )
        );
        breakPointWidth += productCardWidth;
        slidesToShow = (breakPointWidth - spacingBuffer) / productCardWidth - 1;
      }
      responsive.push(
        createBreakPoint(9999999, slidesToShow, displayedRecommendations)
      );

      const navClass =
        containerHeight && containerHeight > 0
          ? "merchZone-half-nav"
          : "merchZone-nav";

      $merchZone.not(".slick-initialized").slick({
        dots: false,
        infinite: false,
        speed: 300,
        prevArrow:
          '<div class="' +
          navClass +
          ' left"><svg class="MuiSvgIcon-root" focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"></path></svg></div>',
        nextArrow:
          '<div class="' +
          navClass +
          ' right"><svg class="MuiSvgIcon-root" focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"></path></svg></div>',
        variableWidth: true,
        responsive: responsive,
      });
      $(".slick-list").addClass("merch-list");

      updateItemAvailibility(
        displayAvailability,
        pricedItemIdentifiers,
        merchZoneItemsNode,
        productAvailabilityAPIName,
        coid
      );
    }
  }
}
function addToCart(itemIdentifier: IItemIdentifier, targetElement: Element) {
  let addQty = 1;

  const targetContainer = targetElement.parentElement?.parentElement;

  const qtyElement: HTMLInputElement | null | undefined =
    targetContainer?.querySelector(".mzs-qty-add-to-cart");

  const qtyElementContent = qtyElement?.value;

  if (qtyElementContent) {
    const qtyElementValue = parseInt(qtyElementContent);

    if (qtyElement && !isNaN(qtyElementValue)) {
      addQty = qtyElementValue;
    }
  }

  setSpinnerElement(targetElement);

  const itemsToAdd: ICartItemToAdd[] = [
    {
      itemIdentifier: itemIdentifier,
      qty: addQty,
      notes: "",
      isPostSupercedeValidation: false,
    },
  ];

  CartServiceAccessor()
    .addCartItems(itemsToAdd, itemsToAdd.length === 1)
    .subscribe({
      next: () => setSpinnerElement(targetElement),
      error: (error) => {
        setSpinnerElement(targetElement);
        console.error(error);
      },
    });
}

function selectQtyInputOnFocus() {
  const qtyInputs: NodeListOf<HTMLInputElement> = document.querySelectorAll(
    ".mzs-qty-add-to-cart"
  );
  qtyInputs?.forEach((el) => {
    el.addEventListener("focus", function () {
      if (el.value) {
        el.select();
      }
    });
  });
}

function updateItemAvailibility(
  displayAvailability: boolean,
  pricedItemIdentifiers: IItemIdentifier[],
  merchZoneItemsNode: NodeListOf<Element>,
  productAvailabilityAPIName: string,
  coid: string
) {
  if (displayAvailability && pricedItemIdentifiers.length) {
    WbmMainHeaderApi.getShoppingLocation().subscribe({
      next: (shoppingLocation: IShoppingLocation) => {
        const dlvMode =
          shoppingLocation?.dlvMode || shoppingLocation?.dlvModeId;

        const altAddressRecId = shoppingLocation
          ? shoppingLocation.mbsAltAddressRecId || shoppingLocation.displayId
          : "";

        const state = shoppingLocation?.state;
        const zipCode = shoppingLocation?.zipCode;

        const items = pricedItemIdentifiers.map((item) => {
          return {
            unitId: item.unitId,
            itemId: item.id,
          };
        });

        const accountNumber = sessionStorage.getItem(mzsAccountNumber);

        if (dlvMode && accountNumber) {
          ProductAvailabilityService.get(
            dlvMode,
            productAvailabilityAPIName,
            altAddressRecId,
            items,
            accountNumber,
            state,
            zipCode
          ).subscribe({
            next: (response: IAvailabilityInfo[]) => {
              renderItemAvailibility(response, merchZoneItemsNode, coid);
            },
            error: (e: any) => {
              console.error(e);
            },
          });
        }
      },
      error: (e: any) => {
        console.error(e);
      },
    });
  }
}

function renderItemAvailibility(
  response: IAvailabilityInfo[],
  merchZoneItemsNode: NodeListOf<Element>,
  coid: string
) {
  merchZoneItemsNode.forEach((itemElement) => {
    const itemIdentifier = parseItemIdentifier(itemElement, coid);

    if (itemIdentifier) {
      const itemAvailability = response.find((item) => {
        return (
          item.itemId === itemIdentifier.id &&
          item.unitId === itemIdentifier.unitId
        );
      });

      if (itemAvailability) {
        const availibilityElement = itemElement.querySelector(
          ".mzs-availibility-container"
        );

        if (availibilityElement) {
          availibilityElement.textContent = ""; //reset content when subscription triggers
          availibilityElement.setAttribute("style", "display:block;");

          const messageElement = document.createElement("div");
          messageElement.className = `${"mzs-availability-"}${AlertLevel[
            itemAvailability.alertLevel
          ].toLowerCase()}`;

          availibilityElement.appendChild(messageElement);

          const deliveryMessageElement = document.createElement("span");

          if (itemAvailability.doPreventAddToCart) {
            deliveryMessageElement.className = "mzs-prevent-purchase";
            deliveryMessageElement.innerHTML =
              itemAvailability.preventPurchaseMessage;

            messageElement.appendChild(deliveryMessageElement);
          } else {
            if (itemAvailability.calloutMessage) {
              const calloutElement = document.createElement("div");
              calloutElement.innerHTML = itemAvailability.calloutMessage;

              messageElement.appendChild(calloutElement);
            }

            if (itemAvailability.wbTruckDelivery) {
              const wbTruckElement = document.createElement("img");
              wbTruckElement.alt = "truck";
              wbTruckElement.src = "https://images.wbmason.com/65/wb_truck.png";

              messageElement.appendChild(wbTruckElement);
            }

            deliveryMessageElement.innerHTML = itemAvailability.deliveryMessage;

            if (itemAvailability.requiresMessageDescriptions) {
              const messageAnchorElement = document.createElement("a");

              messageAnchorElement.addEventListener("click", (event) => {
                event.stopPropagation();

                var windowPopup = window.open(
                  "https://www.wbmason.com/PopupPage.aspx?cpi=Availability",
                  "",
                  "menubar=0,scrollbars=1,toolbar=0,resizable=0,status=0,location=0,width=345,height=400"
                );
                windowPopup?.focus();
                windowPopup?.moveTo(30, 30);
              });

              messageAnchorElement.appendChild(deliveryMessageElement);
              messageElement.appendChild(messageAnchorElement);
            } else {
              messageElement.appendChild(deliveryMessageElement);
            }
          }
        }
      }
    }
  });
}

export const MerchZonesService = {
  priceAndRenderRecommendations(
    merchZoneId: string,
    coid: string = "",
    containerWidth: number = 1366,
    containerHeight: number = 0
  ): void {
    const merchZoneElement = document.querySelector(`#${merchZoneId}`);

    if (merchZoneElement) {
      const merchZoneItemsNode =
        merchZoneElement.querySelectorAll(".merchZone-item");
      const itemIdentifiers = getItemIdentifiers(merchZoneItemsNode, coid);

      if (itemIdentifiers.length) {
        selectQtyInputOnFocus();
        forkJoin([
          pricePermitPurchaseItems(itemIdentifiers),
          ConfigPropService.get([
            ConfigProps.DisplayAvailability,
            ConfigProps.ProductAvailabilityAPIName,
            ConfigProps.QuantityMaxDigits,
          ]),
        ]).subscribe({
          next: (responses: [IPricedProduct[], Record<string, string>]) => {
            const configProps = responses[1];
            const displayAvailability = ConfigPropService.isYesOrTrue(
              configProps[ConfigProps.DisplayAvailability]
            );
            const productAvailabilityAPIName =
              configProps[ConfigProps.DisplayAvailability];
            const quantityMaxDigits =
              configProps[ConfigProps.QuantityMaxDigits];

            updateRecommendations(
              responses[0],
              merchZoneItemsNode,
              merchZoneElement,
              merchZoneId,
              containerWidth,
              containerHeight,
              displayAvailability,
              productAvailabilityAPIName,
              quantityMaxDigits,
              coid
            );
          },
          error: (e: any) => {
            console.error(e);
          },
        });
      }
    }
  },
};
